import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "analytics_session_id";

function getSessionId(): string {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

export function useTrackEvent() {
  const track = useCallback(
    async (
      eventType: string,
      category: string = "general",
      data: Record<string, unknown> = {},
      pagePath?: string
    ) => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        await supabase.from("analytics_events").insert({
          user_id: user.id,
          event_type: eventType,
          event_category: category,
          event_data: data,
          page_path: pagePath ?? window.location.pathname,
          session_id: getSessionId(),
        } as any);
      } catch {
        // silent fail for analytics
      }
    },
    []
  );

  return track;
}

export function usePageViewTracker() {
  const location = useLocation();
  const track = useTrackEvent();
  const lastPath = useRef<string>("");

  useEffect(() => {
    if (location.pathname !== lastPath.current) {
      lastPath.current = location.pathname;
      track("page_view", "navigation", { path: location.pathname }, location.pathname);
    }
  }, [location.pathname, track]);
}

export function useFeatureTracker() {
  const track = useTrackEvent();

  const trackFeature = useCallback(
    (featureName: string, metadata: Record<string, unknown> = {}) => {
      track("feature_use", "engagement", { feature: featureName, ...metadata });
    },
    [track]
  );

  return trackFeature;
}
